import { useEffect, useState } from 'react';
import * as Y from 'yjs';
import { attachLocalPersistence } from '../../sync/localPersistence';
import { P2pProvider } from '../../sync/p2pProvider';
import { instantiateTemplateShapes, seedShapes, type Shape } from '@frame/shared';
import { findTemplate } from '../../templates/registry';
import {
  clearPendingTemplate,
  readPendingTemplate,
} from '../../templates/pendingTemplate';
import type { ConnectionStatus, PeerInfo } from '../../sync/types';

export type { ConnectionStatus } from '../../sync/types';

export interface BoardDoc {
  doc: Y.Doc;
  provider: P2pProvider;
}

// Owns the Y.Doc, IndexedDB persistence, and P2P provider for one board.
// All three live INSIDE useEffect (not via useMemo) so React 18 StrictMode's
// mount→unmount→remount cycle cleanly tears down each instance — useMemo
// would memoize a now-destroyed provider across the remount, leaving an
// orphaned RTCPeerConnection and an awareness ghost on remote peers.
//
// Returns `null` for `bundle` until IndexedDB has finished replaying any
// stored updates. Consumers (BoardPage) gate the rest of the canvas behind
// this so shapes don't pop in after the first render.
//
// `localUserId` is the stable per-browser id used for both awareness identity
// and signaling-payload `peerId`; passing it in lets one component own user
// identity (useLocalUser) and another own sync.
export function useBoardDoc(boardId: string, localUserId: string) {
  const [bundle, setBundle] = useState<BoardDoc | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('loading');
  const [peerCount, setPeerCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const doc = new Y.Doc();
    const persistence = attachLocalPersistence(boardId, doc);
    const provider = new P2pProvider(doc, localUserId);

    const onPeers = (peers: PeerInfo[]) => {
      setPeerCount(peers.length);
      setStatus(peers.length > 0 ? 'live' : 'ready');
    };
    provider.on('peers-changed', onPeers);

    persistence.ready.then(() => {
      if (cancelled) return;
      // Seed an empty board so users see something to interact with on first
      // open. With no backend, seeding moves from server to client. If the
      // user created this board through the template picker, the pending
      // marker tells us which template's shapes to stamp; absent that, we
      // fall back to the legacy seed (kept so existing tests / dev boards
      // still get content on first open).
      seedIfEmpty(doc, boardId, localUserId);
      setBundle({ doc, provider });
      setStatus(provider.listPeers().length > 0 ? 'live' : 'ready');
    });

    return () => {
      cancelled = true;
      provider.off('peers-changed', onPeers);
      provider.destroy();
      persistence.destroy();
      doc.destroy();
      setBundle(null);
      setPeerCount(0);
      setStatus('loading');
    };
  }, [boardId, localUserId]);

  return { bundle, status, peerCount };
}

function seedIfEmpty(doc: Y.Doc, boardId: string, authorId: string): void {
  const shapesMap = doc.getMap<Y.Map<unknown>>('shapes');
  // Always clear the pending marker once we've reached this point — whether
  // we seed or not, the marker has served its purpose; leaving it would
  // re-seed on every refresh that happens to land on an emptied board.
  const pending = readPendingTemplate(boardId);
  clearPendingTemplate(boardId);

  if (shapesMap.size > 0) return;

  const shapes = resolveSeedShapes(pending, authorId);
  doc.transact(() => {
    for (const shape of shapes) {
      const ymap = new Y.Map<unknown>();
      for (const [key, value] of Object.entries(shape)) ymap.set(key, value);
      shapesMap.set(shape.id, ymap);
    }
  }, 'seed');
}

// If the user picked a template at creation time, use it (instantiated with
// fresh ids + author). Blank template intentionally yields no shapes — the
// EmptyBoardHint then mounts. If there's no pending marker (e.g. opening
// an old board fresh in a new browser), fall back to the legacy demo seed
// so dev environments still have content to play with.
function resolveSeedShapes(pendingTemplateId: string | null, authorId: string): Shape[] {
  if (pendingTemplateId) {
    const tpl = findTemplate(pendingTemplateId);
    if (tpl) return instantiateTemplateShapes(tpl, { authorId });
  }
  return seedShapes;
}
